import React, { useState } from 'react';
import { useHolidayManagement } from '../hooks/useHolidayManagement';
import { HolidayDto, HolidayForm } from '../types/holiday.types';
import HolidayModal from './HolidayModal';
import ConfirmModal from '@/shared/components/feedback/ConfirmModal';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import '../styles/HolidayManagementPage.css';

const PAGE_SIZE = 15;

const HolidayManagementPage: React.FC = () => {
    const {
        holidays,
        loading,
        yearFilter,
        setYearFilter,
        syncing,
        saveHoliday,
        deleteHoliday,
        syncHolidays,
    } = useHolidayManagement();

    const [nameFilter, setNameFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [modalOpen, setModalOpen] = useState(false);
    const [editTarget, setEditTarget] = useState<HolidayDto | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<HolidayDto | null>(null);

    const handleOpenAdd = () => {
        setEditTarget(null);
        setModalOpen(true);
    };

    const handleOpenEdit = (holiday: HolidayDto) => {
        setEditTarget(holiday);
        setModalOpen(true);
    };

    const handleSave = async (form: HolidayForm) => {
        const success = await saveHoliday(form);
        if (success) {
            setModalOpen(false);
            setCurrentPage(1);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        const success = await deleteHoliday(deleteTarget.seqNo);
        if (success) setDeleteTarget(null);
    };

    const handleYearFilterChange = (year: string) => {
        setYearFilter(year);
        setCurrentPage(1);
    };

    const dowLabel = (dow: number) =>
        ['월', '화', '수', '목', '금', '토', '일'][dow - 1] ?? '-';

    const filteredHolidays = (nameFilter.trim()
            ? holidays.filter(h => h.holidayName.includes(nameFilter.trim()))
            : holidays
    ).slice().sort((a, b) => a.holidayDate.localeCompare(b.holidayDate));

    const totalPages = Math.ceil(filteredHolidays.length / PAGE_SIZE);
    const pagedHolidays = filteredHolidays.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    return (
        <div className="holiday-page-content">
            <div className="holiday-page-header">
                <h1 className="holiday-page-title">공휴일 관리</h1>
                <div className="holiday-header-right">
                    <input
                        type="number"
                        className="holiday-year-input"
                        value={yearFilter}
                        onChange={e => handleYearFilterChange(e.target.value)}
                        placeholder="연도"
                    />
                    <input
                        type="text"
                        className="holiday-search-input"
                        value={nameFilter}
                        onChange={e => { setNameFilter(e.target.value); setCurrentPage(1); }}
                        placeholder="공휴일명 검색"
                    />
                    <button
                        className="holiday-sync-btn"
                        onClick={syncHolidays}
                        disabled={syncing}
                    >
                        {syncing ? '동기화 중...' : '자동 동기화'}
                    </button>
                    <button className="holiday-add-btn" onClick={handleOpenAdd}>
                        + 공휴일 추가
                    </button>
                </div>
            </div>

            <div className="holiday-result-count">
                <i className="fas fa-list" />
                총 <strong>{filteredHolidays.length}</strong>개의 공휴일
            </div>

            <div className="holiday-table-wrap">
                {loading ? (
                    <div className="holiday-empty">불러오는 중...</div>
                ) : filteredHolidays.length === 0 ? (
                    <div className="holiday-empty">
                        {nameFilter.trim() ? '검색 결과가 없습니다.' : '등록된 공휴일이 없습니다.'}
                    </div>
                ) : (
                    <table className="holiday-table">
                        <thead>
                        <tr>
                            <th>날짜</th>
                            <th>공휴일명</th>
                            <th>적용 요일</th>
                            <th>관리</th>
                        </tr>
                        </thead>
                        <tbody>
                        {pagedHolidays.map(h => (
                            <tr key={h.seqNo}>
                                <td>{h.holidayDate}</td>
                                <td className="text-left">{h.holidayName}</td>
                                <td>{dowLabel(h.targetDow)}</td>
                                <td>
                                    <button className="holiday-btn-edit" onClick={() => handleOpenEdit(h)}>수정</button>
                                    <button className="holiday-btn-delete" onClick={() => setDeleteTarget(h)}>삭제</button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                )}
            </div>

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
            />

            <HolidayModal
                isOpen={modalOpen}
                editTarget={editTarget}
                onSave={handleSave}
                onClose={() => setModalOpen(false)}
            />

            <ConfirmModal
                isOpen={!!deleteTarget}
                title="공휴일 삭제"
                message={`'${deleteTarget?.holidayName}' (${deleteTarget?.holidayDate})을 삭제하시겠습니까?`}
                confirmText="삭제"
                cancelText="취소"
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteTarget(null)}
            />
        </div>
    );
};

export default HolidayManagementPage;
